import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../api/ApiProvider";
import type { SearchResult } from "../api/types";
import { resolvePastedLink, routes, searchResultHref } from "../lib/links";
import { Icon } from "./ui/Icon";
import type { IconName } from "./ui/Icon";
import { Spinner } from "./ui/Spinner";
import { SearchScopeToggle } from "./SearchScopeToggle";
import { filterByScope, useSearchScope } from "../lib/searchScope";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 220;
const MAX_SHOWN = 8;

export const KIND_LABEL: Record<SearchResult["kind"], string> = {
  customer: "Customer",
  site: "Job",
  supplier: "Supplier",
  document: "Document",
};

export const KIND_ICON: Record<SearchResult["kind"], IconName> = {
  customer: "building",
  site: "briefcase",
  supplier: "truck",
  document: "file",
};

function isMac(): boolean {
  return (
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad/.test(navigator.platform)
  );
}

/**
 * Top-bar search: debounced live results from /api/search (metadata only), arrow-key navigation,
 * Enter opens the highlighted hit (or the full results page), Ctrl/Cmd+K focuses it from anywhere.
 * A pasted Oracle deep link or account number jumps straight to the record.
 */
/**
 * `scopeInline` shows the silo toggle under the box (home hero); otherwise it lives in the popover.
 */
export function GlobalSearch({
  autoFocus = false,
  scopeInline = false,
}: {
  autoFocus?: boolean;
  scopeInline?: boolean;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const [scope, setScope] = useSearchScope();
  const q = value.trim();

  // Ctrl/Cmd+K focuses the box from anywhere.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Debounced live search.
  useEffect(() => {
    if (q.length < MIN_CHARS || resolvePastedLink(q)) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    const t = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      api
        .search(q, controller.signal)
        .then((res) => {
          if (controller.signal.aborted) return;
          setResults(res.results.slice(0, MAX_SHOWN));
          setActive(-1);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setError(err instanceof Error ? err.message : "Search failed");
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(t);
      controller.abort();
    };
  }, [api, q]);

  // Close when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const direct = useMemo(() => resolvePastedLink(q), [q]);
  const grouped = useMemo(() => {
    const scoped = filterByScope(results, scope);
    const records = scoped.filter((r) => r.kind !== "document");
    const documents = scoped.filter((r) => r.kind === "document");
    return { records, documents, flat: [...records, ...documents] };
  }, [results, scope]);
  const searchHref = (query: string) =>
    scope === "all"
      ? routes.search(query)
      : `${routes.search(query)}&kind=${scope}`;

  const go = (href: string) => {
    setOpen(false);
    setValue("");
    inputRef.current?.blur();
    navigate(href);
  };

  const submit = () => {
    if (!q) return;
    if (direct) return go(direct);
    if (active >= 0 && grouped.flat[active])
      return go(searchResultHref(grouped.flat[active]));
    go(searchHref(q));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, grouped.flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      } else if (value) {
        setValue("");
      } else {
        inputRef.current?.blur();
      }
    }
  };

  const showPop = open && q.length > 0;
  const activeId = active >= 0 ? `${listId}-opt-${active}` : undefined;

  return (
    <div className="gsl-search" ref={rootRef} role="search">
      {scopeInline ? (
        <SearchScopeToggle
          value={scope}
          onChange={setScope}
          className="gsl-search-scope-inline"
        />
      ) : null}
      <label htmlFor={`${listId}-input`} className="sr-only">
        Search customers, jobs, suppliers and documents
      </label>
      <div className="relative">
        <Icon name="search" className="gsl-search-icon" size={17} />
        <input
          ref={inputRef}
          id={`${listId}-input`}
          className="gsl-search-input"
          placeholder="Search customers, jobs, suppliers, documents, or paste an Oracle link"
          value={value}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={showPop}
          aria-controls={`${listId}-list`}
          aria-activedescendant={activeId}
          aria-autocomplete="list"
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {!value ? (
          <span className="gsl-search-hint" aria-hidden="true">
            <span className="gsl-kbd">{isMac() ? "⌘" : "Ctrl"} K</span>
          </span>
        ) : (
          <button
            type="button"
            className="gsl-btn gsl-btn-ghost gsl-btn-sm gsl-btn-icon absolute right-1.5 top-1/2 -translate-y-1/2"
            aria-label="Clear search"
            onClick={() => {
              setValue("");
              inputRef.current?.focus();
            }}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {showPop ? (
        <div
          className="gsl-search-pop"
          id={`${listId}-list`}
          role="listbox"
          aria-label="Search suggestions"
        >
          {!scopeInline && !direct ? (
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <SearchScopeToggle value={scope} onChange={setScope} size="sm" />
            </div>
          ) : null}
          {direct ? (
            <button
              type="button"
              className="gsl-search-item"
              role="option"
              aria-selected="true"
              onClick={() => go(direct)}
            >
              <Icon name="link" size={16} className="text-muted-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">
                  Open this record
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {direct}
                </span>
              </span>
              <span className="gsl-kbd">Enter</span>
            </button>
          ) : q.length < MIN_CHARS ? (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              Keep typing to search.
            </p>
          ) : loading && results.length === 0 ? (
            <div className="px-4 py-3">
              <Spinner label={`Searching for “${q}”`} />
            </div>
          ) : error ? (
            <p className="px-4 py-3 text-sm text-destructive">{error}</p>
          ) : grouped.flat.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              {results.length > 0 && scope !== "all"
                ? `Nothing in ${scopeLabel(scope)} matches “${q}”. Switch to All to widen the search.`
                : `Nothing matches “${q}”. Search covers names, numbers, job references and document titles only.`}
            </p>
          ) : (
            <>
              {grouped.records.length > 0 ? (
                <p className="gsl-search-group">Records</p>
              ) : null}
              {grouped.records.map((r, i) => (
                <ResultItem
                  key={`${r.kind}-${r.id}`}
                  r={r}
                  id={`${listId}-opt-${i}`}
                  selected={active === i}
                  onPick={() => go(searchResultHref(r))}
                />
              ))}
              {grouped.documents.length > 0 ? (
                <p className="gsl-search-group">Documents</p>
              ) : null}
              {grouped.documents.map((r, j) => {
                const i = grouped.records.length + j;
                return (
                  <ResultItem
                    key={`${r.kind}-${r.id}`}
                    r={r}
                    id={`${listId}-opt-${i}`}
                    selected={active === i}
                    onPick={() => go(searchResultHref(r))}
                  />
                );
              })}
            </>
          )}
          {!direct && q.length >= MIN_CHARS ? (
            <div className="gsl-search-foot">
              <span>
                <span className="gsl-kbd">↑↓</span> to move,{" "}
                <span className="gsl-kbd">Enter</span> to open
              </span>
              <button
                type="button"
                className="gsl-link text-xs"
                onClick={() => go(searchHref(q))}
              >
                All results for “{q}”
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function scopeLabel(scope: string): string {
  return scope === "customer"
    ? "Customers"
    : scope === "site"
      ? "Jobs"
      : scope === "supplier"
        ? "Suppliers"
        : "All";
}

function ResultItem({
  r,
  id,
  selected,
  onPick,
}: {
  r: SearchResult;
  id: string;
  selected: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      id={id}
      role="option"
      aria-selected={selected}
      className="gsl-search-item"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onPick}
    >
      <Icon
        name={KIND_ICON[r.kind]}
        size={16}
        className="shrink-0 text-muted-foreground"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">
          <span className="font-medium">{r.title}</span>
          {r.subtitle ? (
            <span className="text-muted-foreground"> — {r.subtitle}</span>
          ) : null}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {KIND_LABEL[r.kind]}
          {r.kind !== "document" && r.number ? (
            <span className="gsl-num"> {r.number}</span>
          ) : null}
          {r.branchCode ? ` · ${r.branchCode}` : ""}
        </span>
      </span>
      {r.status ? (
        <span className="text-xs text-muted-foreground">{r.status}</span>
      ) : null}
    </button>
  );
}
